/**
 * ProtectedRoute — redirige vers /auth/login si non connecté.
 *
 * Refonte 9.P.1 — loading state aligné brand : KollectiviaMark mono + label
 * bilingue + slate-50 fond + min-h-dvh.
 */

import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";

import { useAuth } from "../contexts/AuthContext";

import { KollectiviaMark } from "./brand/KollectiviaMark";
import { MembershipAccess } from "./MembershipAccess";

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { user, loading, membershipStatus, contextError, refresh, signOut, contextRevision } =
    useAuth();

  if (loading) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="grid min-h-dvh place-items-center bg-slate-50"
      >
        <div className="flex flex-col items-center gap-3 text-sm text-slate-500">
          <KollectiviaMark
            size={96}
            variant="mono"
            glow={false}
            ariaLabel="Kollectivia"
            animate="all"
          />
          <span>{t("app.loading")}</span>
        </div>
      </div>
    );
  }

  if (contextError)
    return (
      <main className="grid min-h-dvh place-items-center bg-slate-50 px-4">
        <section
          role="alert"
          className="max-w-lg space-y-4 rounded-xl border border-amber-200 bg-white p-6"
        >
          <h1 className="text-xl font-semibold">{t("membership.context_failed")}</h1>
          <button
            type="button"
            onClick={() => void refresh()}
            className="text-brand-700 min-h-11 rounded-md px-4 focus-visible:ring-2"
          >
            {t("membership.retry")}
          </button>
          <button
            type="button"
            onClick={() => void signOut()}
            className="min-h-11 rounded-md px-4 focus-visible:ring-2"
          >
            {t("membership.sign_out")}
          </button>
        </section>
      </main>
    );
  if (!user) return <Navigate to="/auth/login" replace />;
  if (user.role !== "super_admin" && membershipStatus !== "active") return <MembershipAccess />;
  return (
    <div key={contextRevision} className="contents">
      {children}
    </div>
  );
}
